import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth } from "./firebaseConfig";
import google from "../../../assets/google.jpeg";
import { usePostHog } from "posthog-js/react";

const GoogleForm = ({ onClose, setLoading, loading, onError }) => {
  const provider = new GoogleAuthProvider();
  const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;
  const posthog = usePostHog();

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken(true);

      const tryFetch = async (retry = false) => {
        const response = await fetch(`${apiBaseURL}/google-signup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_token: idToken }),
        });

        if (!response.ok && !retry) {
          await new Promise((r) => setTimeout(r, 500));
          return tryFetch(true);
        }

        return response;
      };

      const response = await tryFetch();
      const data = await response.json();

      if (response.ok) {
      onClose(data);

      if (posthog) {
        posthog.capture("Google Login Success", {
          email: data.email,
          user_id: data.user_id,
          source: "frontend",
        });
      }
    }
    else {
        posthog.capture("Google Signup Backend Failed", {
          response_status: response.status,
          response_body: data,
          source: "backend",
        });
        onError(data.detail || "Signup failed. Please try again.");
      }
    } catch (error) {
      posthog.capture("Google Login Failed", {
        error_code: error.code || "unknown",
        error_message: error.message || "unknown",
        source: "frontend",
      });

      console.error("Google authentication failed:", error);

      if (error.code === "auth/popup-closed-by-user") {
        onError("Popup was closed. Please try again.");
      } else if (error.code === "auth/cancelled-popup-request") {
        // Retry once
        try {
          const retryResult = await signInWithPopup(auth, provider);
          const retryUser = retryResult.user;
          const idToken = await retryUser.getIdToken(true);
          const response = await fetch(`${apiBaseURL}/google-signup`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id_token: idToken }),
          });

          const data = await response.json();
          if (response.ok) {
            onClose(data);
          } else {
            posthog.capture("Google Signup Backend Failed (Retry)", {
              response_status: response.status,
              response_body: data,
              source: "backend",
            });
            onError(data.detail || "Signup failed after retry.");
          }
        } catch (retryError) {
          posthog.capture("Google Login Retry Failed", {
            error_code: retryError.code || "unknown",
            error_message: retryError.message || "unknown",
            source: "frontend",
          });
          onError("We couldn't log you in. Please try again.");
        }
      } else {
        onError("We couldn't log you in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={() => {
        setLoading(true);
        handleGoogleLogin();
      }}
      className="flex items-center justify-center space-x-4 mb-4 px-4 py-2 bg-white duration-300 border border-gray-300 rounded-lg min-w-[250px]"
      disabled={loading}
    >
      {loading ? (
        <span className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
      ) : (
        <>
          <img src={google} alt="Google logo" className="w-6 h-6" />
          <span className="text-gray-500 font-medium text-lg font-gotham font-medium">
            Continue with Google
          </span>
        </>
      )}
    </button>
  );
};

export default GoogleForm;
